import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { siteInfo } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db8fvn3k13ra8mig8r4g";

interface FormStatus {
  state: "idle" | "submitting" | "success" | "error";
  message: string;
}

export default function AboutPage() {
  const [status, setStatus] = useState<FormStatus>({ state: "idle", message: "" });

  usePageMeta({
    title: "关于我们 - 吃瓜爆料",
    description: `${siteInfo.name}的网站定位、使命、联系方式与免责声明，欢迎投稿与反馈。`,
    keywords: "关于吃瓜爆料,联系方式,投稿,免责声明",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus({ state: "success", message: "感谢你的反馈，我们已收到。" });
      form.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    if (!name || !email) {
      setStatus({ state: "error", message: "请填写称呼和邮箱后再提交。" });
      return;
    }
    if (message.length > 500) {
      setStatus({ state: "error", message: "留言内容不能超过 500 字。" });
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      const v = String(value).trim();
      if (key === "website_alt" && !v) return;
      payload.append(key, String(value));
    });

    setStatus({ state: "submitting", message: "" });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || parsed?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (!ok || isSpam) {
        setStatus({
          state: "error",
          message: serverMsg && !isSpam ? String(serverMsg) : "提交失败，请稍后再试。",
        });
        return;
      }

      setStatus({ state: "success", message: "感谢你的反馈，我们已收到，会尽快查看。" });
      form.reset();
    } catch {
      setStatus({ state: "error", message: "网络异常，提交失败，请稍后再试。" });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="w-full px-4 md:px-8 py-6">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <section className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10 mb-8">
          <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-4">
            <i className="ri-information-line" /> 关于 {siteInfo.name}
          </span>
          <h1 className="text-2xl md:text-4xl font-black text-foreground-950 leading-snug">关于我们</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-4 max-w-3xl leading-relaxed">{siteInfo.description}</p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7">
              <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-flag-line text-primary-600" /> 网站定位与使命
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  {siteInfo.name}是一个聚合娱乐八卦与社会新闻热点的资讯门户。我们希望用清晰的结构和清爽的阅读体验，
                  让每一位读者都能在碎片时间里，快速了解正在发生的事。
                </p>
                <p>
                  我们相信，资讯既要“快”，也要“稳”。无论是明星动态、社会热点，还是事件背后的多角度解析，
                  我们都尽量把来龙去脉讲清楚，而不是只给一个博眼球的标题。
                </p>
                <p>
                  未来，我们会继续打磨分类浏览、关键词搜索与专题集纳等能力，让“吃瓜”这件事也能更省心、更理性。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                {[
                  { icon: "ri-flashlight-line", title: "及时", desc: "热点第一时间聚合呈现" },
                  { icon: "ri-shield-check-line", title: "严谨", desc: "多角度核实与梳理" },
                  { icon: "ri-heart-3-line", title: "好读", desc: "清爽排版，专注阅读" },
                ].map((v) => (
                  <div key={v.title} className="p-4 rounded-lg bg-background-100">
                    <span className="w-9 h-9 rounded-md bg-background-50 flex items-center justify-center text-primary-600 mb-3">
                      <i className={`${v.icon} text-lg`} />
                    </span>
                    <h3 className="text-sm font-bold text-foreground-950">{v.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1.5 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7">
              <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-chat-3-line text-primary-600" /> 给我们留言
              </h2>
              <p className="text-sm text-foreground-600 mb-5 leading-relaxed">
                有爆料线索、合作意向，或者只是想吐槽一下？欢迎给我们留言，我们会认真阅读每一条反馈。
              </p>

              <form data-readdy-form id="about-message-form" onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="field-extra-verify"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      你的称呼 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="方便我们回复你"
                      className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="写下你想说的话（不超过 500 字）"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={status.state === "submitting"}
                    className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
                  >
                    {status.state === "submitting" ? "提交中..." : "提交留言"}
                  </button>
                  {status.message && (
                    <span
                      className={`text-sm ${
                        status.state === "error" ? "text-accent-700" : "text-primary-700"
                      }`}
                    >
                      {status.message}
                    </span>
                  )}
                </div>
              </form>
            </section>

            <section className="rounded-lg bg-secondary-100 border border-secondary-200 p-5 md:p-7">
              <h2 className="text-xl font-bold text-secondary-950 mb-4 flex items-center gap-2">
                <i className="ri-error-warning-line text-primary-600" /> 使用说明与免责声明
              </h2>
              <ul className="space-y-3 text-sm text-secondary-800 leading-relaxed">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
                  本站为产品演示站点，页面中的新闻标题、图片、数据均为虚构内容，不代表任何真实人物或事件。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
                  全站搜索、筛选与排序均为前端演示效果，不接入真实后端接口。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
                  分享功能仅提供“复制链接”本地演示，不会调用任何第三方社交平台。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
                  请理性看待网络信息，涉及他人隐私与名誉的内容请勿传播。
                </li>
              </ul>
            </section>
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <h2 className="text-lg font-bold text-foreground-950 mb-4">联系方式</h2>
              <ul className="space-y-4 text-sm text-foreground-700">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500 text-xs mb-0.5">邮箱</span>
                    <a href={`mailto:${siteInfo.email}`} className="text-foreground-950 hover:text-primary-600 break-all">
                      {siteInfo.email}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500 text-xs mb-0.5">地址</span>
                    <span className="text-foreground-950 leading-relaxed">{siteInfo.address}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className="ri-time-line" />
                  </span>
                  <span>
                    <span className="block text-foreground-500 text-xs mb-0.5">内容更新</span>
                    <span className="text-foreground-950">每日 09:00 - 23:00 持续更新</span>
                  </span>
                </li>
              </ul>
            </section>

            <section className="rounded-lg bg-primary-500 p-5 md:p-6 text-background-50">
              <h2 className="text-lg font-bold mb-2">有料想爆？</h2>
              <p className="text-sm text-background-100 leading-relaxed">
                如果你手上有值得关注的热点线索，欢迎通过邮箱联系我们。请务必确认信息真实、来源可靠。
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-background-50 text-primary-700 text-sm font-medium whitespace-nowrap mt-4 hover:bg-primary-50 cursor-pointer"
              >
                <i className="ri-send-plane-line" /> 联系编辑部
              </a>
            </section>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}